import { useDefaultImportMap } from '@softarc/native-federation-orchestrator/options';

/**
 * Shape of a native import map. Kept local: @softarc/native-federation-orchestrator does not
 * re-export its ImportMap type from the public entry.
 */
type ImportMap = {
    imports: Record<string, string>;
    scopes?: Record<string, Record<string, string>>;
    integrity?: Record<string, string>;
};

const nativeImportMap = useDefaultImportMap();

/**
 * initFederation always calls setImportMapFn with `{ override: true }`, which would delete the
 * build-time-inlined <script type="importmap"> in index.html and re-register the SAME host keys
 * at runtime. Browsers that merge multiple import maps null-block a redefined specifier even
 * when both maps agree on the value (BX-23098). Host shared deps are already in that inlined
 * map; remotes (mfe-messages) can contribute keys the host never shared (stream-chat, etc.).
 *
 * Ignore override, drop keys already in the DOM, and append only the remainder. If nothing is
 * new, skip writing a second map entirely.
 */
export function appendNewImportMapKeys(importMap: ImportMap, _opts?: { override?: boolean }): Promise<ImportMap> {
    const existing = readExistingImportMaps();
    const filtered = diffImportMap(importMap, existing);
    if (isEmpty(filtered)) {
        return Promise.resolve(importMap);
    }
    return nativeImportMap.setImportMapFn(filtered);
}

type ExistingMaps = {
    imports: Set<string>;
    scopes: Map<string, Set<string>>;
};

function readExistingImportMaps(): ExistingMaps {
    const imports = new Set<string>();
    const scopes = new Map<string, Set<string>>();
    if (typeof document === 'undefined') {
        return { imports, scopes };
    }

    for (const script of Array.from(document.querySelectorAll('script[type="importmap"]'))) {
        try {
            const parsed = JSON.parse(script.textContent || '{}') as ImportMap;
            for (const specifier of Object.keys(parsed.imports ?? {})) {
                imports.add(specifier);
            }
            for (const [scopeUrl, mappings] of Object.entries(parsed.scopes ?? {})) {
                let specifiers = scopes.get(scopeUrl);
                if (!specifiers) {
                    specifiers = new Set<string>();
                    scopes.set(scopeUrl, specifiers);
                }
                for (const specifier of Object.keys(mappings)) {
                    specifiers.add(specifier);
                }
            }
        } catch {
            // Unparseable script: skip it rather than crash bootstrap. A duplicate append of
            // those keys would re-introduce the BX-23098 null-block, so only parsed maps count.
        }
    }

    return { imports, scopes };
}

function diffImportMap(incoming: ImportMap, existing: ExistingMaps): ImportMap {
    const imports: Record<string, string> = {};
    for (const [specifier, url] of Object.entries(incoming.imports ?? {})) {
        if (!existing.imports.has(specifier)) {
            imports[specifier] = url;
        }
    }

    const scopes: Record<string, Record<string, string>> = {};
    for (const [scopeUrl, mappings] of Object.entries(incoming.scopes ?? {})) {
        const alreadyMapped = existing.scopes.get(scopeUrl);
        const next: Record<string, string> = {};
        for (const [specifier, url] of Object.entries(mappings)) {
            if (!alreadyMapped?.has(specifier)) {
                next[specifier] = url;
            }
        }
        if (Object.keys(next).length > 0) {
            scopes[scopeUrl] = next;
        }
    }

    const addedUrls = new Set<string>([...Object.values(imports), ...Object.values(scopes).flatMap((mapping) => Object.values(mapping))]);
    const integrity: Record<string, string> = {};
    for (const [url, hash] of Object.entries(incoming.integrity ?? {})) {
        if (addedUrls.has(url)) {
            integrity[url] = hash;
        }
    }

    const filtered: ImportMap = { imports };
    if (Object.keys(scopes).length > 0) {
        filtered.scopes = scopes;
    }
    if (Object.keys(integrity).length > 0) {
        filtered.integrity = integrity;
    }
    return filtered;
}

function isEmpty(importMap: ImportMap): boolean {
    return Object.keys(importMap.imports).length === 0 && Object.keys(importMap.scopes ?? {}).length === 0;
}
